/**
 * Edit material modal
 */

import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, TreeSelect, message } from 'antd';
import { materialApi, folderApi } from '../../services/resourceService';
import { ResourceScope, ResourceFolder, FolderType, Material, MaterialType } from '../../types/resource';

const { Option } = Select;

interface EditMaterialModalProps {
  visible: boolean;
  material: Material | null;
  onCancel: () => void;
  onSuccess: () => void;
}

const EditMaterialModal: React.FC<EditMaterialModalProps> = ({
  visible,
  material,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [folders, setFolders] = useState<ResourceFolder[]>([]);

  // Load folders and set form values
  useEffect(() => {
    if (visible && material) {
      loadFolders();
      form.setFieldsValue({
        name: material.name,
        folder_id: material.folder_id,
        tags: material.tags ? material.tags.join(', ') : '',
      });
    }
  }, [visible, material]);

  const loadFolders = async () => {
    if (!material) return;
    try {
      const result = await folderApi.getTree(FolderType.MATERIAL, material.scope);
      setFolders(result);
    } catch (error) {
      console.error('Failed to load folders:', error);
    }
  };

  // Convert folders to tree data
  const buildTreeData = (folders: ResourceFolder[]): any[] => {
    return folders.map((folder) => ({
      title: folder.name,
      value: folder.id,
      children: folder.children ? buildTreeData(folder.children) : [],
    }));
  };

  const handleSubmit = async () => {
    if (!material) return;

    try {
      const values = await form.validateFields();
      setLoading(true);

      await materialApi.update(material.id, {
        name: values.name,
        folder_id: values.folder_id || null,
        tags: values.tags ? values.tags.split(',').map((t: string) => t.trim()) : [],
      });

      message.success('更新成功');
      form.resetFields();
      onSuccess();
    } catch (error: any) {
      if (error.errorFields) {
        return;
      }
      message.error(error.message || '更新失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="编辑素材"
      open={visible}
      onOk={handleSubmit}
      onCancel={onCancel}
      confirmLoading={loading}
      width={600}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
      >
        <Form.Item
          name="name"
          label="名称"
          rules={[{ required: true, message: '请输入名称' }]}
        >
          <Input placeholder="请输入素材名称" />
        </Form.Item>

        <Form.Item name="folder_id" label="分组">
          <TreeSelect
            placeholder="请选择分组（可选）"
            allowClear
            treeData={buildTreeData(folders)}
          />
        </Form.Item>

        <Form.Item name="tags" label="标签">
          <Input placeholder="多个标签用逗号分隔" />
        </Form.Item>

        <Form.Item label="文件信息">
          <div style={{ color: 'rgba(0, 0, 0, 0.45)' }}>
            <div>文件类型: {material?.file_type}</div>
            <div>文件大小: {material ? formatFileSize(material.file_size) : '-'}</div>
            <div style={{ marginTop: 8, fontSize: 12 }}>
              注：暂不支持替换文件，如需更换请删除后重新上传
            </div>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

// Format file size helper
const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default EditMaterialModal;
